import { Injectable } from "@angular/core";
import { HttpHeaders } from "@angular/common/http";
import { throwError } from "rxjs";
import { MatSnackBar } from "@angular/material/snack-bar";
// import { tokenNotExpired } from 'angular2-jwt';
import { Router } from "@angular/router";
import { ResourceCacheService } from "./resource-cache.service";

@Injectable()
export class AuthService {
  // Must match SideNavComponent's own FLOATS_HIDDEN_STORAGE_KEY - it's a pure client-side
  // layout preference (whether the Open Door/Support/Contact quick-action tabs are hidden), not
  // auth/session data, so it's carried across the localStorage.clear() below in logout()/
  // adminLogout() rather than being wiped like the rest of a logged-out session's storage.
  private static readonly SIDE_NAV_QUICK_ACTIONS_KEY = 'sideNavQuickActionsHidden';

  authToken: any;
  user: any;
  userData: any;

  constructor(public snackbar: MatSnackBar, public router: Router, private resourceCache: ResourceCacheService) { }

  // Function to create headers, add token, to be used in HTTP requests
  createAuthenticationHeaders() {
    const token = this.loadToken();
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        "authorization": token
      }),
    };
    httpOptions;
  }

  createHeaders() {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
    return httpOptions;
  }

  createAuthorizationHeader(headers: HttpHeaders) {
    headers.append("Authorization", this.loadToken());
  }

  // Function to get token from client local storage
  loadToken() {
    this.authToken = localStorage.getItem("token");
    return this.authToken;
  }

  loadUserData() {
    this.userData = localStorage.getItem("userDetails");
    return this.userData;
  }

  // Function to logout
  logout() {
    this.resourceCache.clearAll();
    const quickActionFloatsHidden = localStorage.getItem(AuthService.SIDE_NAV_QUICK_ACTIONS_KEY);
    localStorage.clear();
    if (quickActionFloatsHidden !== null) {
      localStorage.setItem(AuthService.SIDE_NAV_QUICK_ACTIONS_KEY, quickActionFloatsHidden);
    }
    sessionStorage.clear();
    this.router.navigate(["/login"]);
  }

  // Function to logout
  adminLogout() {
    this.resourceCache.clearAll();
    const quickActionFloatsHidden = localStorage.getItem(AuthService.SIDE_NAV_QUICK_ACTIONS_KEY);
    localStorage.clear();
    if (quickActionFloatsHidden !== null) {
      localStorage.setItem(AuthService.SIDE_NAV_QUICK_ACTIONS_KEY, quickActionFloatsHidden);
    }
    sessionStorage.clear();
    this.router.navigate(["/admin-login"]);
  }

  // Function to store user's data in client local storage
  storeUserData(token, userData) {
    localStorage.setItem("token", token);
    localStorage.setItem("userDetails", JSON.stringify(userData));
    localStorage.setItem(
      "accessRestriction",
      JSON.stringify(userData.accessRestriction)
    );
    localStorage.setItem(
      "dashboardAccessRestriction",
      userData.dashboardAccessRestriction
    );
    localStorage.setItem("userIsLoggedIn", "yes");
  }

  // Shared by LoginComponent (password login) and AutoLoginComponent (SSO token
  // exchange) - both hit an API that resolves the same { Status, adminData, jwt }
  // shape, and from there need identical setup: store the session, decide which
  // dashboard route to land on, and hold navigation if the plan is expired so the
  // caller can show the sub-expired popup instead.
  completePanelLogin(res: any): { subExpired: boolean; popupData?: any } {
    const lastDashboardView = localStorage.getItem('gymDashboardView');
    const route = res.adminData.setNewDashActive
      ? (lastDashboardView === 'admin' ? 'gym/dashboard/v2' : 'gym/dashboard/staff')
      : 'gym/dashboard';

    if (res.adminData.showPopup) {
      const popupData = {
        'daysRemaining': res.adminData?.daysRemaining,
        'discount': res.adminData?.discount,
        'pendingBalance': res.adminData?.fytPlanPendingBalance,
        'dueDate': res.adminData?.fytPlanDueDate,
        'adminPlanId': res.adminData?.adminPlanId
      };
      if (res.adminData?.fytPlanPendingBalance > 0) this.storeUserData(res.jwt, res.adminData);

      // Only hold navigation here if subscription is EXPIRED (days <= 0). If
      // it's just expiring (days > 0), let the SideNav handle it once they
      // enter the dashboard.
      if (res.adminData.daysRemaining <= 0) {
        return { subExpired: true, popupData };
      }
    }

    this.storeUserData(res.jwt, res.adminData);
    localStorage.setItem("popupFlag", JSON.stringify(true));
    const accessRestriction = localStorage.getItem('accessRestriction');
    if (accessRestriction === 'undefined') {
      this.router.navigate([route]);
    } else {
      accessRestriction.replace(/'/g, '"');
      const aR = JSON.parse(accessRestriction);
      if (aR[0]) {
        if (aR[0] === 'dashboard') this.router.navigate(['/gym/enquiry']);
        else this.router.navigate([route]);
      } else this.router.navigate([route]);
    }
    return { subExpired: false };
  }

  // Function to check if user is logged in
  loggedIn(currentUrl) {
    // console.log('currentUrl', currentUrl);

    // get the auth token from localStorage
    let token = localStorage.getItem("token");
    let accessList = localStorage.getItem("accessList");
    let accessRestriction = localStorage.getItem("accessRestriction");
    let dashboardAccessRestriction = localStorage.getItem(
      "dashboardAccessRestriction"
    );
    // console.log('accessList', accessList);
    // console.log('accessRestriction', accessRestriction);
    // console.log('dashboardAccessRestriction', dashboardAccessRestriction);

    if (accessList && accessList.length > 0 && accessList.includes(currentUrl))
      return "Resticted";
    if (accessRestriction && accessRestriction.includes(currentUrl))
      return "Resticted";
    if (
      dashboardAccessRestriction &&
      dashboardAccessRestriction.includes(currentUrl)
    )
      return "Resticted";

    // check if token is set, then...
    if (token) {
      return "true";
    }
    return "false";
    // return "false";
  }

  getCheckError(error: any) {
    // var errMsg = "Warning - ";
    // this.snackbar.open(errMsg, error.Message, { duration: 3000 });
    if (error.status === 401) {
      this.logout();
      return throwError(false);
    } else if (error.status === 429) {
      const errMsg = error.error?.message || 'Too many attempts. Please try again after some time.';
      return throwError(errMsg);
    } else if (error.status === 403) {
      // this.snackbar.open(
      //   "Warning",
      //   "You are not authorised to perform this action.",
      //   { duration: 3000 }
      // );
      // setTimeout(() => { this.logout() }, 4000)/
      return error;
    } else {
      // Prefer the backend's own JSON body message (error.error.message — this app's
      // controllers pervasively respond with {statusCode, status, message} on failure)
      // over error.message, which on an HttpErrorResponse is just Angular's generic
      // "Http failure response for <url>: <code> <text>" wrapper text, not anything the
      // backend actually said. Strictly better than before: falls back to the exact
      // same behavior as previously whenever the backend didn't send a message.
      const errMsg = error.error?.message
        ? error.error.message
        : error.message
          ? error.message
          : error.status
            ? `${error.status} - ${error.statusText}`
            : "Server error";
      // console.error(errMsg);
      return throwError(errMsg);
    }
  }

  // Used by login/auto-login only - those screens must never surface raw
  // backend/network error text (e.g. "Http failure response for
  // http://localhost:3001/signup/panelLogin: 0 Unknown Error") to the user.
  // Derives the display message from the ORIGINAL HttpErrorResponse (not
  // getCheckError's output, which for the generic case is exactly that raw
  // text) - only the 429 rate-limit reason is specific enough to be worth
  // showing; everything else collapses to one fixed, friendly message.
  toSafeAuthError(error: any): string {
    if (error?.status === 429) {
      return error.error?.message || 'Too many attempts. Please try again after some time.';
    }
    return 'Login failed. Please try again.';
  }
}
